// src/tattoo/components/FlashGalleryBrowser.jsx
//
// Editor-side flash picker. Lives in the left panel's Flash section
// (desktop) and is the modal pane the user sees in the screenshot at
// docs/FLASH_GALLERY_plan.md — "Browse available flash and place it on
// your plannequin."
//
// Data sources (merged into one grid):
//   1. DB-backed flash from public.flash via listAvailableFlashGlobal()
//      — all `available` pieces from every active, entitled shop.
//   2. Static "Tattune Free Flash" from /flash/tattune/manifest.json
//      — Shep's curated freebie library, refreshed monthly outside
//      the app. Rendered with a prominent "Free" badge, no price,
//      and no "View artist" affordance since they're house designs.
//
// Click behaviour: stashes the chosen flash via the existing
// stashPendingFlash flow + dispatches `tt-pending-flash` so the editor
// picks it up immediately (the picker is mounted inside the editor —
// no navigation needed). The watermark from makeWatermarkedThumbWithLogo
// is applied to the grid thumbnail; the editor strips it when the
// design is placed on the model (separate pipeline).

import React, { useEffect, useMemo, useState } from "react";
import { stashPendingFlash } from "../lib/flashUtils";
import { makeWatermarkedThumbWithLogo } from "../lib/watermarkThumbs";
import { listAvailableFlashGlobal, formatPrice } from "../lib/flash";

const WM_LOGO_URL = "/tt-watermark.png";
const TATTUNE_FREE_MANIFEST = "/flash/tattune/manifest.json";

// Sort options. Newest is default — keeps fresh uploads + monthly
// Tattune drops visible.
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
];

export default function FlashGalleryBrowser() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [wmThumbs, setWmThumbs] = useState({});

  // ---------- Load: DB flash + Tattune Free Flash in parallel ----------

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const [dbRows, freebies] = await Promise.all([
          listAvailableFlashGlobal(),
          fetchTattuneFree(),
        ]);

        if (cancelled) return;

        const dbCards = dbRows.map(mapDbRowToCard);
        // Freebies first, then DB by created_at desc (the query already
        // ordered them that way).
        const all = [...freebies, ...dbCards];
        setEntries(all);
      } catch (e) {
        console.error("[flash gallery] load failed", e);
        if (!cancelled) setError("Couldn't load flash right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Watermark thumbs in the background ----------

  useEffect(() => {
    let cancelled = false;

    async function watermark() {
      const next = {};
      for (const item of entries) {
        const url = item.url;
        if (!url) continue;
        if (wmThumbs[url]) continue;
        try {
          next[url] = await makeWatermarkedThumbWithLogo(url, WM_LOGO_URL);
        } catch (e) {
          console.warn("[flash gallery] watermark failed", url, e);
        }
        if (cancelled) return;
      }
      if (!cancelled && Object.keys(next).length) {
        setWmThumbs((prev) => ({ ...prev, ...next }));
      }
    }

    watermark();
    return () => {
      cancelled = true;
    };
  }, [entries, wmThumbs]);

  // ---------- Filter + sort ----------

  const filteredEntries = useMemo(() => {
    const term = filter.trim().toLowerCase();
    let list = entries;

    if (term) {
      list = list.filter((item) => {
        const haystack = [
          item.title,
          item.artistName,
          item.sizeLabel,
          item.priceCents != null ? String(item.priceCents / 100) : null,
          ...(item.styleTags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    if (sort === "price_asc") {
      list = [...list].sort(
        (a, b) =>
          (a.priceCents ?? Number.POSITIVE_INFINITY) -
          (b.priceCents ?? Number.POSITIVE_INFINITY)
      );
    } else if (sort === "price_desc") {
      list = [...list].sort((a, b) => (b.priceCents ?? -1) - (a.priceCents ?? -1));
    }
    // "newest" — already in load() order (freebies + DB by created_at desc).

    return list;
  }, [entries, filter, sort]);

  // ---------- Use a flash ----------

  function handleUseFlash(item) {
    try {
      const ok = stashPendingFlash({
        url: item.url,
        name: item.title,
        price: item.priceCents != null ? item.priceCents / 100 : null,
        size: item.sizeLabel,
        tags: item.styleTags || [],
        ownerShop: item.ownerShop || null,
      });
      if (!ok) return;
      window.dispatchEvent(new Event("tt-pending-flash"));
    } catch (e) {
      console.warn("[flash gallery] use flash failed", e);
    }
  }

  // ---------- Render ----------

  return (
    <div className="tt-lead-card" id="flash-gallery-browser">
      <h3 className="tt-lead-title">Flash gallery</h3>
      <p className="tt-lead-subtitle">
        Browse available flash and place it on your plannequin.
      </p>
      <p style={{ fontSize: 11, opacity: 0.6, marginTop: -4 }}>
        Tap a design to place it on the mannequin.
      </p>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <input
          className="tt-lead-input"
          placeholder="Filter by name, artist, style, or size…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ flex: "1 1 220px", minWidth: 0 }}
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          style={selectStyle}
          aria-label="Sort"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              Sort: {opt.label}
            </option>
          ))}
        </select>
      </div>

      {loading && <div className="tt-lead-status">Loading flash…</div>}
      {error && !loading && <div className="tt-lead-error">{error}</div>}
      {!loading && !error && filteredEntries.length === 0 && (
        <div className="tt-lead-status">
          {filter ? "No flash matches that filter." : "No flash available yet."}
        </div>
      )}

      {!loading && !error && filteredEntries.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 10,
            gridAutoRows: "1fr",
          }}
        >
          {filteredEntries.map((item) => (
            <FlashCard
              key={item.id}
              item={item}
              displayUrl={wmThumbs[item.url] || item.url}
              onPickup={() => handleUseFlash(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FlashCard — single grid cell
// =============================================================================

function FlashCard({ item, displayUrl, onPickup }) {
  const isHeld = item.status === "held";
  const isFree = item.isFreebie;
  const priceLabel = isFree
    ? "Free"
    : formatPrice(item.priceCents, item.currency);
  const sizeLabel = item.sizeLabel || null;
  const topStyles = (item.styleTags || []).slice(0, 2);

  return (
    <div style={cardWrapStyle}>
      <button
        type="button"
        onClick={onPickup}
        style={cardButtonStyle}
        title={`Place ${item.title || "this flash"} on the plannequin`}
      >
        <div style={imageWrapStyle}>
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={item.title || "Flash"}
              loading="lazy"
              style={imageStyle}
            />
          ) : (
            <div style={imagePlaceholderStyle}>…</div>
          )}

          {item.isOneOfOne && !isFree && (
            <div style={oneOfOneBadgeStyle}>1/1</div>
          )}
          {isHeld && <div style={heldBadgeStyle}>Held</div>}
          {isFree && <div style={freeBadgeStyle}>Free</div>}
        </div>

        <div style={cardBodyStyle}>
          <div style={titleStyle}>{item.title || "Untitled flash"}</div>
          {!isFree && item.artistName && (
            <div style={artistStyle}>{item.artistName}</div>
          )}
          <div style={metaStyle}>
            {priceLabel}
            {sizeLabel ? ` · ${sizeLabel}` : ""}
          </div>
          {topStyles.length > 0 && (
            <div style={chipRowStyle}>
              {topStyles.map((tag) => (
                <span key={tag} style={chipStyle}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </button>

      {!isFree && item.artistSlug && (
        <a
          href={`/artists/${item.artistSlug}`}
          target="_blank"
          rel="noopener noreferrer"
          style={viewArtistStyle}
          onClick={(e) => e.stopPropagation()}
        >
          View artist
        </a>
      )}
    </div>
  );
}

// =============================================================================
// Helpers — data mapping + Tattune freebie fetch
// =============================================================================

function mapDbRowToCard(row) {
  const shop = row.shop || null;
  return {
    id: `db::${row.id}`,
    url: row.url,
    title: row.title || "Untitled flash",
    priceCents: row.price_cents,
    currency: shop?.currency || "CAD",
    sizeLabel: row.size_label || null,
    styleTags: Array.isArray(row.style_tags) ? row.style_tags : [],
    status: row.status,
    isOneOfOne: !!row.is_one_of_one,
    isFreebie: false,
    artistName: shop?.display_name || shop?.name || null,
    artistSlug: shop?.slug || null,
    ownerShop: shop
      ? {
          id: shop.id,
          name: shop.display_name || shop.name || null,
          slug: shop.slug || null,
          display_name: shop.display_name,
        }
      : null,
  };
}

async function fetchTattuneFree() {
  try {
    const res = await fetch(TATTUNE_FREE_MANIFEST, { cache: "no-store" });
    if (!res.ok) return [];
    const manifest = await res.json();
    if (!Array.isArray(manifest)) return [];
    return manifest.map((item, idx) => ({
      id: `tattune-free::${item.file || item.url || idx}`,
      url: normalizeFileUrl(item.file || item.url),
      title: item.name || item.title || "Tattune Free Flash",
      priceCents: 0,
      currency: "CAD",
      sizeLabel: item.size || item.sizeLabel || null,
      styleTags: Array.isArray(item.tags) ? item.tags : [],
      status: "available",
      isOneOfOne: false,
      isFreebie: true,
      artistName: "Tattune",
      artistSlug: null,
      ownerShop: null,
    }));
  } catch (e) {
    console.warn("[flash gallery] tattune free fetch failed", e);
    return [];
  }
}

function normalizeFileUrl(file) {
  if (!file) return null;
  if (file.startsWith("/flash/")) return file;
  if (file.startsWith("/tattoo/flash/")) return file.replace("/tattoo", "");
  if (!file.startsWith("/")) return file;
  return file;
}

// =============================================================================
// Styles — kept inline so the component is self-contained.
// =============================================================================

const selectStyle = {
  fontSize: 13,
  color: "#fff",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 8,
  padding: "7px 10px",
  outline: "none",
};

const cardWrapStyle = {
  display: "flex",
  flexDirection: "column",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.10)",
  background: "#141414",
  transition:
    "transform 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease",
};

const cardButtonStyle = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: 0,
  border: "none",
  background: "transparent",
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
  flex: 1,
};

const imageWrapStyle = {
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",
  background: "#fff",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
};

const imagePlaceholderStyle = {
  width: "100%",
  height: "100%",
  display: "grid",
  placeItems: "center",
  color: "rgba(0,0,0,0.3)",
  fontSize: 14,
};

const oneOfOneBadgeStyle = {
  position: "absolute",
  top: 6,
  right: 6,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#fff",
  background: "rgba(0,0,0,0.6)",
  padding: "2px 7px",
  borderRadius: 999,
};

const heldBadgeStyle = {
  position: "absolute",
  top: 6,
  left: 6,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#fff",
  background: "rgba(202,138,4,0.85)",
  padding: "2px 7px",
  borderRadius: 999,
};

const freeBadgeStyle = {
  position: "absolute",
  top: 6,
  left: 6,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#fff",
  background: "rgba(162,51,43,0.95)",
  padding: "2px 8px",
  borderRadius: 999,
};

const cardBodyStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "10px 12px 12px",
  flex: 1,
};

const titleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#fff",
  lineHeight: 1.25,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const artistStyle = {
  fontSize: 11,
  color: "rgba(255,255,255,0.65)",
  lineHeight: 1.3,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const metaStyle = {
  fontSize: 12,
  color: "rgba(255,255,255,0.75)",
  marginTop: 2,
};

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  marginTop: 4,
};

const chipStyle = {
  fontSize: 10,
  color: "rgba(255,255,255,0.7)",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.10)",
  padding: "1px 7px",
  borderRadius: 999,
  whiteSpace: "nowrap",
  textTransform: "capitalize",
};

const viewArtistStyle = {
  fontSize: 11,
  fontWeight: 500,
  color: "rgba(255,107,93,0.9)",
  textDecoration: "none",
  padding: "8px 12px",
  textAlign: "center",
  borderTop: "1px solid rgba(255,255,255,0.06)",
};
